import { AssetInfo } from '@ergolabs/ergo-sdk/build/main/entities/assetInfo';
import axios, { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import {
  combineLatest,
  filter,
  first,
  map,
  Observable,
  publishReplay,
  refCount,
  switchMap,
  tap,
} from 'rxjs';

import { applicationConfig } from '../../applicationConfig';
import { addresses$ } from '../addresses/addresses';
import { balance$ } from '../balance/balance';
import { networkContext$ } from '../networkContext/networkContext';

type VestingRound = 'community' | 'public';

export interface RawVestingItem {
  readonly 'Next unlock': string;
  readonly Redeemable: number;
  readonly Remaining: number;
  readonly 'Vesting Key Id': string;
  readonly boxId: string;
}

export interface VestingItem {
  readonly nextUnlock: DateTime;
  readonly redeemable: number;
  readonly remaining: number;
  readonly vestingTokenId: string;
  readonly boxId: string;
  readonly round: VestingRound;
}

export interface Vesting {
  readonly community: VestingItem[];
  readonly public: VestingItem[];
}

export const vestingItems$: Observable<Vesting> = networkContext$.pipe(
  switchMap(() =>
    addresses$.pipe(
      filter((addresses) => addresses.length > 0),
      first(),
    ),
  ),
  switchMap((addresses) =>
    axios.post(`${applicationConfig.ergopadUrl}/vesting/v2/`, {
      addresses,
    }),
  ),
  map<AxiosResponse, RawVestingItem[]>((res) => res.data.SPF),
  switchMap((rawVestingItems) =>
    combineLatest(
      rawVestingItems.map((rvi) =>
        balance$.pipe(
          map(
            (balance) =>
              [balance.get({ id: rvi['Vesting Key Id'] })?.asset, rvi] as [
                AssetInfo,
                RawVestingItem,
              ],
          ),
        ),
      ),
    ),
  ),
  map<[AssetInfo, RawVestingItem][], Vesting>((rawVestingItems) =>
    rawVestingItems.reduce<Vesting>(
      (acc, [asset, rvi]) => {
        const round = asset.name?.toLowerCase().includes('public')
          ? 'public'
          : 'community';

        acc[round].push({
          nextUnlock: DateTime.fromISO(rvi['Next unlock']),
          boxId: rvi.boxId,
          redeemable: rvi.Redeemable,
          remaining: rvi.Remaining,
          vestingTokenId: rvi['Vesting Key Id'],
          round,
        });

        return acc;
      },
      { public: [], community: [] },
    ),
  ),
  publishReplay(1),
  refCount(),
);
